import React, { useState } from 'react';
import { ButtonWithDropdown, Icon } from '@seeqdev/qomponents';
import { useTranslation } from 'react-i18next';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { setActiveTool, setItem } from '@/toolSelection/investigate.actions';
import { errorToast } from '@/utilities/toast.utilities';
import { fetchModel } from '@/tools/models/model.utilities';
import { MODEL_TYPES } from '@/tools/models/model.types';
import { SelectedSearchItem } from '@/search/search.types';
import { isScalingTableEditorEnabled } from '@/services/systemConfiguration.utilities';
import { CreateApplyInScalingTableModal } from '@/tools/models/CreateApplyInScalingTableModal.molecule';
import { setDisplayTableDefinitionEditor } from '@/worksheet/worksheet.actions';
import { setAutoFocusTableDefinitionTab } from '@/main/layout.actions';
import { resetTableDefinition, setIsApplyingModel } from '@/tableDefinitionEditor/tableDefinition.actions';
import { resetItemFinder } from '@/itemFinder/itemFinder.actions';
import { resetTableEditorStore } from '@/tableDefinitionEditor/tableDefinitionEditor.actions';
import { EDITOR_TYPES } from '@/tableDefinitionEditor/tableDefinition.types';
import { editTableDefinition } from '@/tableDefinitionEditor/tableDefinition.utilities';

export interface ModelRowItem {
  isAssetGroupItem: boolean;
  includedProperties: Record<string, any>;
  parameters: any[];
  id: string;
  name: string;
  type: 'Model';
  ancestors: any[];
  hasChildren: boolean;
  hasActiveChildren: boolean;
  isArchived: boolean;
  isRedacted: boolean;
}

interface ModelItemMenuProps {
  item: ModelRowItem;
}

export const ModelItemMenu: React.FunctionComponent<ModelItemMenuProps> = (props) => {
  const { item } = props;
  const { t } = useTranslation();
  const editorType = EDITOR_TYPES.SCALING;

  const [isItemSearchModalOpen, setIsItemSearchModalOpen] = useState(false);
  const [selectedScalingTable, setSelectedScalingTable] = useState<SelectedSearchItem | undefined>(undefined);

  const cancelScalingTableSelection = () => {
    setIsItemSearchModalOpen(false);
    setSelectedScalingTable(undefined);
  };

  return (
    <>
      <ButtonWithDropdown
        triggerIcon={
          <Icon type="theme" icon="fc-more" extraClassNames="btn-transparent" testId="modelItemMenuButton" />
        }
        onContainerClick={(e) => e.stopPropagation()}
        dropdownItems={[
          {
            icon: 'fa-info-circle',
            iconType: 'theme',
            testId: 'modelProperties',
            label: t('PROPERTIES.HEADER'),
            onClick: async (e) => {
              e?.stopPropagation();
              try {
                await fetchModel(item.id, MODEL_TYPES.TOOL);
                setActiveTool(TREND_TOOLS.PROPERTIES);
                setItem(item);
              } catch (e) {
                errorToast({ httpResponseOrError: e });
              }
            },
          },
          {
            icon: 'fa-chart-area',
            iconType: 'theme',
            testId: 'applyModelOnce',
            label: t('MODELS.APPLY_MODEL_ONCE'),
            onClick: async (e) => {
              e?.stopPropagation();
              try {
                await fetchModel(item.id, MODEL_TYPES.TOOL);
                setActiveTool(TREND_TOOLS.MODEL_APPLY);
              } catch (e) {
                errorToast({ httpResponseOrError: e });
              }
            },
          },
          ...(isScalingTableEditorEnabled()
            ? [
                {
                  icon: 'fc fc-table',
                  iconType: 'theme',
                  testId: 'applyToScalingTable',
                  label: t('MODELS.APPLY_TO_SCALING_TABLE'),
                  onClick: (e: any) => {
                    e?.stopPropagation();
                    setIsItemSearchModalOpen(true);
                  },
                },
              ]
            : []),
        ]}
      />
      {isItemSearchModalOpen && (
        <div onClick={(e) => e.stopPropagation()}>
          <CreateApplyInScalingTableModal
            header="MODELS.MODAL_HEADER"
            helpText="MODELS.MODAL_HELP_TEXT"
            onClose={cancelScalingTableSelection}
            onSubmit={() => {
              setIsItemSearchModalOpen(false);
              setDisplayTableDefinitionEditor(true);
              setAutoFocusTableDefinitionTab(true);
              if (selectedScalingTable) {
                editTableDefinition(selectedScalingTable, editorType).then(async () => {
                  await fetchModel(item.id, MODEL_TYPES.SCALING);
                  setIsApplyingModel(EDITOR_TYPES.SCALING, true);
                });
              }
              setSelectedScalingTable(undefined);
            }}
            onCreateNewItem={async () => {
              setIsItemSearchModalOpen(false);
              resetItemFinder();
              resetTableDefinition(editorType);
              resetTableEditorStore(editorType);
              setDisplayTableDefinitionEditor(true);
              setAutoFocusTableDefinitionTab(true);
              await fetchModel(item.id, MODEL_TYPES.SCALING);
              setIsApplyingModel(EDITOR_TYPES.SCALING, true);
            }}
            onSelectItem={(item) => setSelectedScalingTable(item)}
            selectedItem={selectedScalingTable}
            iconPartialTooltipKey="MODELS"
          />
        </div>
      )}
    </>
  );
};
